import Swal from "sweetalert2";
import React from "react";
import withReactContent from "sweetalert2-react-content";
import styled from "styled-components";
import "./opnijCustomPopup.scss";
import { darken } from "polished";
import { black, HeaderTitle, inter, primaryPink, secondaryGreenLight, white } from "../Theme";
import {ReactComponent as CloseIcon} from '../resources/icons/close.svg';
import DOMPurify from 'dompurify';
import { device } from "../constants/breakpoints";

export const HelpPopup = withReactContent(Swal);

function showHelpPopup(title, subtitle, items) {
    HelpPopup.fire({
        html: (
            <PopupContent
                title={title}
                subtitle={subtitle}
                items={items}
                onClose={() => HelpPopup.close()}
            />
        ),
        showCancelButton: false,
        showConfirmButton: false,
        width: "662px",
        customClass: {
            container: "swal2-container-custom swal2-container-custom--help",
            htmlContainer: "swal2-html-container-custom",
            popup: "swal2-popup-custom-transparent",
        },
        heightAuto: false,
    });
}

const StyledPopupContent = styled.div`
    max-width: 606px;
    margin: auto;
    background: ${white};
    text-align: start;
`;

const CloseButtonWrapper = styled.div`
    display: flex;
    flex-direction: row-reverse;
    width: 100%;
    padding: 20px 20px 0 20px;
    flex-shrink: 0;
`;

const StyledCloseIcon = styled(CloseIcon)`
    display: block;
    width: 24px;
    height: 24px;
    cursor: pointer;
`;

const Header = styled.div`
    padding: 0 48px;
    margin-bottom: 22px;
    
    @media screen and ${device.tablet} {
        padding: 0 32px;
    }
`;

const Subtitle = styled.p`
    font-family: ${inter};
    font-size: 16px;
    color: ${black};
    margin: 12px 0 0;
`;

const Paragraphs = styled.div`
    padding: 0 48px 48px;
    display: flex;
    flex-direction: column;
    gap: 22px;
    
    @media screen and ${device.tablet} {
        padding: 0 32px 32px;
    }
`;

const HTMLText = styled.div`
    font-family: ${inter};
    font-size: 16px;
    font-weight: 400;
    color: ${black};
    line-height: 21px;
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;

    p {
        margin: 0 0 12px;
    }

    p:last-child {
        margin-bottom: 0;
    }

    a {
        color: ${primaryPink};
        text-decoration: underline;
        text-decoration-color: currentColor;
        text-underline-offset: 2px;
        cursor: pointer;
    }

    a:hover {
        color: ${darken(0.05, primaryPink)};
    }

    ul {
        list-style-type: disc;
        padding-left: 20px;
        margin: 0 0 12px;
    }

    ol {
        list-style-type: decimal;
        padding-left: 20px;
        margin: 0 0 12px;
    }

    li {
        margin-bottom: 6px;
    }

    ul:last-child, ol:last-child, li:last-child {
        margin-bottom: 0;
    }

    strong, b {
        font-weight: 700;
    }

    em, i {
        font-style: italic;
    }

    u {
        text-decoration: underline;
    }

    s, del {
        text-decoration: line-through;
    }
`;

const VideoWrapper = styled.div`
    position: relative;
    width: 100%;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;
    background-color: ${secondaryGreenLight};

    iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: 0;
    }
`;

function PopupContent({ title, subtitle, items, onClose }) {
    return (
        <StyledPopupContent>
            <CloseButtonWrapper>
                <StyledCloseIcon onClick={onClose} />
            </CloseButtonWrapper>

            <Header>
                {title && <HeaderTitle>{title}</HeaderTitle>}
                {subtitle && <Subtitle>{subtitle}</Subtitle>}
            </Header>
            
            {items?.length > 0 && (
                <Paragraphs>
                    {items?.map((item) => {
                        if (item.type === "text" && item.content) {
                            return (
                                <HTMLText
                                    key={item.id}
                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.content) }}
                                />
                            );
                        }

                        if (item.type === "video" && item.youtubeUrl) {
                            return (
                                <VideoWrapper key={item.id}>
                                    <iframe
                                        title="YouTube-video"
                                        src={item.youtubeUrl}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowFullScreen
                                    />
                                </VideoWrapper>
                            );
                        }

                        return null;
                    })}
                </Paragraphs>
            )}
        </StyledPopupContent>
    );
}

export default showHelpPopup;
